import { useSyncExternalStore } from 'react';
import type { CookieConsentConfig } from 'vanilla-cookieconsent';
import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * @desc The consent cookie shared with the legacy site (H2-204).
 *
 * h2gc.com's banner is the same `vanilla-cookieconsent` v3 with library-default cookie settings:
 * `cc_cookie`, 182 days, `SameSite=Lax`, path `/`, revision 0. This module restates those defaults
 * explicitly so the contract is pinned rather than inherited — a visitor who consented on h2gc.com
 * and is then redirected to the beta portal must not be asked again, which only holds while both
 * sites read and write the identical cookie. The one deliberate difference is `domain`: on
 * production the cookie is issued for `.h2gc.com` (see `VITE_COOKIE_CONSENT_DOMAIN`) so every
 * subdomain shares it; elsewhere it stays host-only.
 */
export const CONSENT_COOKIE_NAME = 'cc_cookie';

/** @desc Library default; restated so the legacy site's expiry cannot drift from ours unnoticed. */
const CONSENT_COOKIE_EXPIRES_AFTER_DAYS = 182;

/**
 * @desc The category whose acceptance gates every tracker in the portal (Datadog RUM today).
 * `necessary` is always on and read-only.
 */
export const ANALYTICS_CATEGORY = 'analytics';

/** @desc The legacy site owns the policy pages; the portal has none of its own yet. */
const LEGACY_SITE_ORIGIN = 'https://www.h2gc.com';
const COOKIE_POLICY_URL = `${LEGACY_SITE_ORIGIN}/cookie-policy`;
const PRIVACY_POLICY_URL = `${LEGACY_SITE_ORIGIN}/privacy-policy`;

export type CookieConsentOptions = {
  /** @desc Cookie `Domain` attribute; omitted means host-only. */
  domain?: string | undefined;
};

type AnalyticsConsentListener = (accepted: boolean) => void;

const listeners = new Set<AnalyticsConsentListener>();

/** @desc Whether the visitor has a valid, current consent that includes analytics. */
export function hasAnalyticsConsent(): boolean {
  return CookieConsent.validConsent() && CookieConsent.acceptedCategory(ANALYTICS_CATEGORY);
}

/**
 * @desc Subscribe to analytics-consent changes. Fires on the first consent and on every later
 * change to the analytics category, with the new state. Returns the unsubscribe function.
 */
export function subscribeAnalyticsConsent(listener: AnalyticsConsentListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** @desc React view of `hasAnalyticsConsent()`, re-rendering as the visitor's choice changes. */
export function useAnalyticsConsent(): boolean {
  return useSyncExternalStore(subscribeAnalyticsConsent, hasAnalyticsConsent);
}

function notifyAnalyticsConsent(): void {
  const accepted = hasAnalyticsConsent();
  for (const listener of listeners) listener(accepted);
}

/**
 * @desc The banner configuration, ported from the legacy site's `cookie-consent.js` verbatim
 * (layout, categories, copy) so the two banners read as one product and share one cookie.
 */
export function buildCookieConsentConfig({ domain }: CookieConsentOptions): CookieConsentConfig {
  return {
    cookie: {
      name: CONSENT_COOKIE_NAME,
      ...(domain ? { domain } : {}),
      path: '/',
      expiresAfterDays: CONSENT_COOKIE_EXPIRES_AFTER_DAYS,
      sameSite: 'Lax',
    },

    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: false,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      [ANALYTICS_CATEGORY]: {
        enabled: false,
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: '_dd_s' }],
        },
      },
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description: `We use essential cookies to keep the site working. With your consent, we also use analytics cookies (Google Analytics, Datadog) to understand how visitors use our site and monitor performance. No data is sold to third parties. <a href="${COOKIE_POLICY_URL}" class="cc__link">Cookie Policy</a>`,
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject optional',
            showPreferencesBtn: 'Manage preferences',
            footer: `<a href="${PRIVACY_POLICY_URL}">Privacy Policy</a> &nbsp;·&nbsp; <a href="${COOKIE_POLICY_URL}">Cookie Policy</a>`,
          },
          preferencesModal: {
            title: 'Cookie Preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            serviceCounterLabel: 'Service|Services',
            sections: [
              {
                title: 'Your privacy choices',
                description:
                  'Use the toggles below to manage which cookies we set. Strictly necessary cookies cannot be disabled as they are required for the site to function. Changes take effect immediately.',
              },
              {
                title: 'Strictly Necessary',
                description:
                  'These cookies are required for core site functionality — maintaining your login session, CSRF protection, and storing your consent preferences. They cannot be switched off.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics & Monitoring',
                description:
                  'These cookies help us understand how visitors use our site (Google Analytics) and monitor site performance (Datadog RUM). All data is aggregated and anonymised. No advertising or third-party profiling.',
                linkedCategory: ANALYTICS_CATEGORY,
                cookieTable: {
                  caption: 'Analytics cookies',
                  headers: {
                    name: 'Cookie',
                    domain: 'Provider',
                    desc: 'Description',
                  },
                  body: [
                    {
                      name: '_ga, _ga_*, _gid',
                      domain: 'Google Analytics',
                      desc: 'Aggregate traffic reporting — distinguishes users and sessions.',
                    },
                    {
                      name: '_dd_s',
                      domain: 'Datadog',
                      desc: 'Groups page-view events into a session for performance monitoring.',
                    },
                  ],
                },
              },
              {
                title: 'More information',
                description: `For any questions about our use of cookies, please <a href="mailto:info@h2gc.com" class="cc__link">contact us</a>. Full details are in our <a href="${COOKIE_POLICY_URL}" class="cc__link">Cookie Policy</a>.`,
              },
            ],
          },
        },
      },
    },

    onConsent: notifyAnalyticsConsent,

    onChange: ({ changedCategories }) => {
      if (changedCategories.includes(ANALYTICS_CATEGORY)) notifyAnalyticsConsent();
    },
  };
}

/**
 * @desc Start the banner. Resolves once the library has read any existing cookie, so
 * `hasAnalyticsConsent()` is meaningful afterwards; a valid prior consent shows nothing.
 */
export function runCookieConsent(options: CookieConsentOptions): Promise<void> {
  return CookieConsent.run(buildCookieConsentConfig(options));
}

/** @desc Open the preferences modal — for a "Cookie settings" affordance in the app chrome. */
export function showCookiePreferences(): void {
  CookieConsent.showPreferences();
}
